import Popper, {PopperProps} from '@material-ui/core/Popper'
import {Input, InputProps} from '@yandex-taxi/go-web-components'
import {commonLabelStyles} from '@yandex-taxi/go-web-components/dist/components/inputs/styles'
import clsx from 'clsx'
import React, {useCallback, useMemo, useState} from 'react'

import {CalendarIcon} from '../../icons'
import {makeStyles, Theme, useTheme} from '../../theme'
import {Calendar} from './Calendar'
import {
  useCalendarValue,
  useHandlers,
  useHelperProps,
  useInputValue,
  usePopup,
  useTimeValue,
  useValueUpdater,
} from './hooks'
import {commonStyles} from './styles'
import {formatInput, getTime, isAfter, isBefore, isValidTime} from './utils'

/** @deprecated */
export type DatePickerProps = {
  value: string
  onChange?: (date: string) => void
  open?: boolean
  classes?: {
    root?: string
    input?: string
    paper?: string
    calendarIcon?: string
    timeInput?: string
  }
  dateFormat?: string
  inputDateFormat?: string
  withTime?: boolean
  className?: string
  min?: Date
  max?: Date
} & Pick<InputProps, 'variant' | 'placeholder' | 'disabled' | 'error' | 'helperText' | 'label' | 'onBlur'> &
  Pick<PopperProps, 'popperOptions' | 'placement'>

type StylesProps = Pick<DatePickerProps, 'withTime' | 'variant'>

const useStyles = makeStyles((theme: Theme) => {
  const common = commonStyles(theme.go)
  const {width, marginLeft} = common.timeInput

  return {
    input: ({withTime}: StylesProps) => ({
      width: withTime ? `calc(100% - ${width + marginLeft}px)` : '100%',
    }),
    labelText: ({variant}: StylesProps) => ({
      ...commonLabelStyles(theme.go, variant),
      display: 'block',
    }),
    ...common,
  }
})

/** @deprecated use `import {DatePicker} from '@logistics-frontend/blocks/v2` */
export function DatePicker({
  value,
  onChange,
  onBlur,
  open,
  variant,
  popperOptions = {
    modifiers: {flip: {enabled: false}},
  },
  placement = 'bottom-start',
  placeholder,
  label,
  disabled,
  error,
  helperText,
  classes,
  dateFormat,
  inputDateFormat = 'L',
  withTime,
  className,
  min,
  max,
}: DatePickerProps) {
  const [inputRef, paperRef, anchorEl] = usePopup()
  const timeInputRef = React.useRef<any>()

  const theme = useTheme()
  const {input, paper, calendarIcon, root, timeInput, labelText} = useStyles({withTime, variant})
  const [focus, setFocus] = useState(false)

  const [inputValue, setInputValue, isValidInputFormat] = useInputValue(value, inputDateFormat, dateFormat)

  const [timeInputValue, setTimeInputValue] = useTimeValue(value, withTime)

  const [calendarValue, setCalendarValue] = useCalendarValue({
    value,
    dateFormat,
  })

  const updater = useValueUpdater({
    value,
    inputValue,
    inputDateFormat,
    dateFormat,
    withTime,
    timeInputValue,
    onChange,
    setCalendarValue,
  })

  const [onClear, onDateChange, onInputValueChange, onTimeInputValueChange] = useHandlers({
    setInputValue,
    setTimeInputValue,
    setCalendarValue,
    onChange,
    updater,
    inputDateFormat,
  })

  const selectedRange: [string | undefined, string | undefined] = useMemo(
    () => [calendarValue, calendarValue],
    [calendarValue],
  )

  const onInputFocus = useCallback(() => {
    setFocus(true)
  }, [setFocus])

  const onInputBlur = useCallback(
    (e: React.FocusEvent<any>) => {
      if (
        e.relatedTarget === inputRef.current ||
        e.relatedTarget === timeInputRef.current ||
        (inputRef.current as Element)?.contains(e.relatedTarget as Node) ||
        (timeInputRef.current as Element)?.contains(e.relatedTarget as Node)
      ) {
        return
      }

      setFocus(false)
      if (!isValidInputFormat) {
        const newInputValue = formatInput(value, inputDateFormat, dateFormat)
        setInputValue(newInputValue)
        updater({newInputValue})
      }

      if (!isValidTime(timeInputValue)) {
        setTimeInputValue(getTime(value))
      }

      onBlur?.(e)
    },
    [
      updater,
      inputRef,
      isValidInputFormat,
      timeInputValue,
      value,
      inputDateFormat,
      dateFormat,
      setInputValue,
      setTimeInputValue,
      onBlur,
    ],
  )

  const formHelperTextProps = useHelperProps(withTime)

  const preventBlur = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
  }, [])

  const isDisabledDate = useCallback(
    (date: string) => {
      if (min && max) {
        return isBefore(date, min, 'day') || isAfter(date, max, 'day')
      }

      if (min) {
        return isBefore(date, min, 'day')
      }

      if (max) {
        return isAfter(date, max, 'day')
      }

      return false
    },
    [max, min],
  )

  return (
    <div className={clsx(root, classes?.root, className)} onBlur={onInputBlur} onFocus={onInputFocus}>
      {label ? <label className={labelText}>{label}</label> : null}
      <Input
        ref={inputRef}
        className={clsx(input, classes?.input)}
        disabled={disabled}
        error={error}
        FormHelperTextProps={formHelperTextProps}
        helperText={helperText}
        iconStart={<CalendarIcon className={clsx(calendarIcon, classes?.calendarIcon)} />}
        onChange={onInputValueChange}
        onClear={onClear}
        placeholder={placeholder}
        value={inputValue}
        variant={variant}
      />
      {withTime && (
        <Input
          ref={timeInputRef}
          className={clsx(timeInput, classes?.timeInput)}
          clearable={false}
          disabled={disabled}
          error={error}
          onChange={onTimeInputValueChange}
          placeholder="00:00"
          value={timeInputValue}
          variant={variant}
        />
      )}
      <Popper
        {...popperOptions}
        anchorEl={anchorEl}
        open={!disabled && (!!open || focus) && !!anchorEl}
        placement={placement}
        style={{zIndex: 1}}
        disablePortal
      >
        <div ref={paperRef} className={clsx(paper, classes?.paper)} dir={theme.direction} onMouseDown={preventBlur}>
          <Calendar
            isDisabledDate={isDisabledDate}
            onDayClick={onDateChange}
            selectedRange={selectedRange}
            value={calendarValue}
          />
        </div>
      </Popper>
    </div>
  )
}
